import { forwardRef, useState } from 'react';
import { Box, Paper, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components/Badge';
import { Trans } from '@libs/i18n';
import { NoteBlankIcon } from '@phosphor-icons/react';

interface AssessmentNameCardProps {
  assessmentName: string;
  onNameChange?: (name: string) => void;
  error?: boolean;
}

export const AssessmentNameCard = observer(
  forwardRef<HTMLDivElement, AssessmentNameCardProps>(
    ({ assessmentName, onNameChange, error }, ref) => {
      const theme = useTheme();
      const [isEditingName, setIsEditingName] = useState(false);

      const handleNameClick = () => {
        setIsEditingName(true);
      };

      const handleNameBlur = () => {
        setIsEditingName(false);
      };

      const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onNameChange?.(event.target.value);
      };

      const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          handleNameBlur();
        }
      };

      return (
        <Paper
          ref={ref}
          elevation={1}
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pb: 2 }}>
              <NoteBlankIcon color={theme.palette.grey[400]} size={20} />
              <Typography variant="textMD_medium">
                <Trans i18nKey="assessments.new.details.title" />
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="textSM_medium">
                <Trans i18nKey="assessments.new.details.name" />
              </Typography>
              <Badge label={<Trans i18nKey="assessments.new.progressFlow.required" />} variant="Required" />
            </Box>
            {isEditingName ? (
              <TextField
                value={assessmentName}
                onChange={handleInputChange}
                onBlur={handleNameBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                fullWidth
                placeholder="PDPL Gap Assessment — September 2025"
                variant="standard"
                sx={{
                  mt: 0.5,
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                    backgroundColor: 'grey.200',
                    borderRadius: 4,
                    border: error
                      ? `1px solid ${theme.palette.error.main}`
                      : '1px solid transparent',
                    py: 1.25,
                    px: 2,
                    minHeight: '2.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                      display: 'none',
                    },
                    '&:after': {
                      display: 'none',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      display: 'none',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'grey.200',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                    '&::placeholder': {
                      opacity: 0.6,
                    },
                  },
                }}
              />
            ) : (
              <Typography
                variant="body2"
                onClick={handleNameClick}
                sx={{
                  fontSize: '0.875rem',
                  mt: 0.5,
                  backgroundColor: 'grey.200',
                  borderRadius: 4,
                  border: error ? `1px solid ${theme.palette.error.main}` : '1px solid transparent',
                  py: 1.25,
                  px: 2,
                  minHeight: '2.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
              >
                {assessmentName || (
                  <span style={{ opacity: 0.6 }}>PDPL Gap Assessment — September 2025</span>
                )}
              </Typography>
            )}
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              <Trans i18nKey="assessments.new.details.note" />
            </Typography>
          </Box>
        </Paper>
      );
    },
  ),
);
